/**
 * Provides the `getTokenIDsFromString` utility function for extracting the content of top-level tokens (enclosed in `{{ }}`) from a given string, and its associated `GetTokenIDsFromStringOptions` type.
 * @module getTokenIDsFromString
 */
/**
 * Extracts the content of top-level tokens (enclosed in `{{ }}`) from a given string.
 * Optionally includes tokens found within simple HTML attribute values.
 * Handles nested tokens by preserving their internal structure within the extracted string.
 *
 * **Behavior:**
 * - Iterates through the input string character by character.
 * - Tracks the nesting depth of `{{ }}` braces. A depth of 0 means outside any token.
 * - **Token Detection:**
 *  - When `{{` is encountered and depth is 0, it starts collecting characters for a top-level token (depth becomes 1).
 *  - When `{{` is encountered and depth is > 0, the literal `{{` is added to the `currentToken` content (for nested tokens).
 *  - When `}}` is encountered and depth is > 1, the literal `}}` is added to the `currentToken` content.
 *  - When `}}` is encountered and depth is 1, it signifies the end of the top-level token:
 *  - Collection stops (depth becomes 0).
 *  - The collected `currentToken` content is potentially added to the results array based on the `includeHtmlAttributes` flag and whether the parser is currently inside a detected HTML attribute value.
 *  - `currentToken` is reset.
 *   - Any characters encountered while `collect` is true (depth > 0) are appended to `currentToken`.
 * - **HTML Attribute Handling (Simple):**
 *  - Detects entering an attribute value when `="` is found *outside* a token.
 *  - Detects exiting an attribute value when `"` is found while inside an attribute.
 *  - Tokens ending within an attribute value are only added to the results if `includeHtmlAttributes` is `true`.
 * - Returns an array containing the extracted top-level token contents (the strings *between* the outermost `{{` and `}}`).
 *
 * @since 6.3 Initial implementation.
 * @param {string} input - The string potentially containing tokens to extract. Can be any string.
 * @param {boolean} [includeHtmlAttributes=false] - If `true`, tokens found directly within double-quoted HTML attribute values (e.g., `attr="{{token}}"`) are included. If `false` (default), such tokens are ignored.
 * @return {string[]} An array of the extracted top-level token contents. Returns an empty array if no top-level tokens are found or if the input is empty.
 *
 * @example Importing the getTokenIDsFromString utility
 * ```typescript
 * import { getTokenIDsFromString } from '@automator/shared/utils/get-token-id-from-string';
 * ```
 *
 * @example Basic token extraction
 * Extracts simple top-level tokens.
 * ```typescript
 * const text = 'Hello {{name}}, welcome to {{place}}!';
 * getTokenIDsFromString( text );
 * // => [ 'name', 'place' ]
 * ```
 *
 * @example Handling nested tokens
 * Preserves the internal structure, including braces and spaces of nested tokens.
 * ```typescript
 * const text = 'Data: {{ part1:part2:{{field}} }} has value {{ simple }}';
 * getTokenIDsFromString( text );
 * // => [ ' part1:part2:{{field}} ', ' simple ' ]
 * ```
 *
 * @example Excluding HTML attribute tokens (default)
 * Ignores tokens within `href="..."` when `includeHtmlAttributes` is false.
 * ```typescript
 * const htmlText = '<a href="{{link_url}}">Click {{link_text}}</a>';
 * getTokenIDsFromString( htmlText );
 * // => [ 'link_text' ]
 * ```
 *
 * @example Including HTML attribute tokens
 * Includes the token within `href="..."` when `includeHtmlAttributes` is true.
 * ```typescript
 * const htmlText = `
 *     <a href="{{link_url}}">
 *         ${ sprintf( __( 'Click %s', 'uncanny-automator' ), '{{link_text}}' ) }
 *     </a>
`;
 * getTokenIDsFromString( htmlText, true );
 * // => [ 'link_url', 'link_text' ]
 * ```
 *
 * @example Adjacent tokens
 * Correctly extracts tokens placed next to each other.
 * ```typescript
 * const text = 'First:{{token1}}{{token2}}:Last';
 * getTokenIDsFromString( text );
 * // => [ 'token1', 'token2' ]
 * ```
 *
 * @example Handling empty input
 * Returns an empty array for an empty input string.
 * ```typescript
 * getTokenIDsFromString( '' );
 * // => []
 * ```
 *
 * @example Handling input with no tokens
 * Returns an empty array if no tokens are found.
 * ```typescript
 * const text = 'Just plain text.';
 * getTokenIDsFromString( text );
 * // => []
 * ```
 *
 * @remarks
 * - This function uses a simple state machine and does not employ complex regex, making it potentially more robust for deeply nested structures but less performant for very large strings compared to fine-tuned regex. The decision on whether we'll keep this or switch to regex will be based on performance.
 * - The HTML attribute detection is basic (looks for `="...`) and might not handle all edge cases of HTML syntax (e.g., unquoted attributes, single quotes, attributes within comments/scripts).
 * - Unclosed tokens (e.g., `{{token`) or tokens ending prematurely will not be captured correctly.
 * - For comprehensive behavior details, refer to the unit tests.
 */
export function getTokenIDsFromString(input, includeHtmlAttributes = false) {
    const tokens = [];
    let depth = 0;
    let currentToken = '';
    let collect = false; // Flag to indicate if we are inside a top-level token {{...}}
    let insideHtmlAttribute = false; // Flag for simple HTML attribute detection ="..."
    for (let i = 0; i < input.length; i++) {
        const char = input[i];
        const nextChar = i + 1 < input.length ? input[i + 1] : '';
        // --- Simple HTML attribute detection ---
        // Check for entering an attribute value: `="`
        if (!collect && char === '=' && nextChar === '"') {
            insideHtmlAttribute = true;
            i++; // Skip the quote character, loop continues to next char
            continue;
        }
        // Check for exiting an attribute value: `"`
        if (insideHtmlAttribute && char === '"') {
            insideHtmlAttribute = false;
            continue; // Skip this quote char, move to the next character
        }
        // --- End HTML attribute detection ---
        // --- Token detection ---
        // Check for starting token: `{{`
        if (char === '{' && nextChar === '{') {
            if (depth === 0) {
                // Entering a top-level token
                collect = true;
            }
            else {
                // Entering a nested token, add braces to currentToken content
                currentToken += '{{';
            }
            depth++;
            i++; // Skip the next '{' character
            continue; // Move to the next character after '{{'
        }
        // Check for ending token: `}}`
        if (char === '}' && nextChar === '}' && collect) { // Only process if collecting
            depth--;
            if (depth === 0) {
                // Exiting the top-level token
                collect = false;
                // Conditionally add the collected token based on HTML attribute context
                if (!insideHtmlAttribute || includeHtmlAttributes) {
                    tokens.push(currentToken);
                }
                currentToken = ''; // Reset for the next potential token
            }
            else {
                // Exiting a nested token, add braces to currentToken content
                currentToken += '}}';
            }
            i++; // Skip the next '}' character
            continue; // Move to the next character after '}}'
        }
        // --- End Token detection ---
        // If currently collecting characters for a token
        if (collect) {
            currentToken += char;
        }
    } // End loop
    return tokens;
}
;
